import { 
  collection, 
  doc, 
  addDoc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from './firebase';

// ===== USERS =====
export const getUsers = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    return usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

export const getUserById = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting user:', error);
    throw error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    await updateDoc(doc(db, 'users', userId), userData);
    return { success: true };
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// Initialize weddings array for existing users who don't have it
export const initializeUserWeddingsArray = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      
      // If the user doesn't have a weddings array, add it
      if (!userData.weddings) {
        console.log('Initializing weddings array for user:', userId);
        await updateDoc(doc(db, 'users', userId), {
          weddings: []
        });
        return { success: true };
      }
      
      return { success: true, message: 'User already has weddings array' };
    }
    
    return { success: false, message: 'User not found' };
  } catch (error) {
    console.error('Error initializing user weddings array:', error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    await deleteDoc(doc(db, 'users', userId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// ===== WEDDINGS =====
export const createWedding = async (weddingData, userId) => {
  console.log('createWedding called with:', { weddingData, userId });
  try {
    if (!userId) {
      console.error('No userId provided to createWedding');
      throw new Error('User ID is required');
    }

    // Verify user exists first
    console.log('Verifying user exists...');
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.error('User document does not exist for ID:', userId);
      throw new Error('User not found');
    }

    // Format the date properly for Firestore
    const formattedData = {
      ...weddingData,
      date: weddingData.date ? new Date(weddingData.date) : new Date(),
      createdBy: userId,
      createdAt: new Date(),
      tables: []
    };
    
    // Add the wedding to the weddings collection
    console.log('Adding wedding to Firestore...');
    const weddingRef = await addDoc(collection(db, 'weddings'), formattedData);
    console.log('Wedding added to Firestore with ID:', weddingRef.id);
    
    // Update the user's weddings array
    console.log('Updating user\'s weddings array...');
    await updateDoc(userRef, {
      weddings: arrayUnion(weddingRef.id)
    });
    console.log('User document updated successfully');

    // Create public version of the wedding
    await updatePublicWedding(weddingRef.id, formattedData);
    console.log('Public wedding document created successfully');
    
    return { id: weddingRef.id, success: true };
  } catch (error) {
    console.error('Error in createWedding:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

export const getWeddings = async (userId = null, isAdmin = false) => {
  try {
    let weddingsQuery;
    
    if (isAdmin) {
      // Admins can see all weddings
      weddingsQuery = collection(db, 'weddings');
    } else {
      // Regular users can only see weddings assigned to them
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      
      if (!userData || !userData.weddings) {
        return [];
      }
      
      // Get weddings where the user is assigned
      weddingsQuery = query(
        collection(db, 'weddings'),
        where('__name__', 'in', userData.weddings)
      );
    }
    
    const weddingsSnapshot = await getDocs(weddingsQuery);
    return weddingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting weddings:', error);
    throw error;
  }
};

export const getWeddingById = async (weddingId) => {
  try {
    const weddingDoc = await getDoc(doc(db, 'weddings', weddingId));
    if (weddingDoc.exists()) {
      return { id: weddingDoc.id, ...weddingDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting wedding:', error);
    throw error;
  }
};

export const updateWedding = async (weddingId, weddingData) => {
  try {
    await updateDoc(doc(db, 'weddings', weddingId), weddingData);
    await updatePublicWedding(weddingId, weddingData);
    return { success: true };
  } catch (error) {
    console.error('Error updating wedding:', error);
    throw error;
  }
};

export const deleteWedding = async (weddingId, userId) => {
  try {
    // Delete the wedding
    await deleteDoc(doc(db, 'weddings', weddingId));
    
    // Delete the public version
    await deleteDoc(doc(db, 'publicWeddings', weddingId));
    
    // Remove the wedding from the user's weddings array
    await updateDoc(doc(db, 'users', userId), {
      weddings: arrayRemove(weddingId)
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting wedding:', error);
    throw error;
  }
};

// ===== TABLES =====
export const addTable = async (weddingId, tableData) => {
  try {
    const weddingRef = doc(db, 'weddings', weddingId);
    const weddingDoc = await getDoc(weddingRef);
    
    if (!weddingDoc.exists()) {
      throw new Error('Wedding not found');
    }
    
    const weddingData = weddingDoc.data();
    const tables = weddingData.tables || [];
    
    // Create a new table with the structure matching your database
    const newTable = {
      tableNumber: tableData.tableNumber,
      members: tableData.members || [] // Initialize with empty members array or provided members
    };
    
    // Add the table to the wedding's tables array
    await updateDoc(weddingRef, {
      tables: [...tables, newTable]
    });
    
    return { tableId: tables.length, success: true };
  } catch (error) {
    console.error('Error adding table:', error);
    throw error;
  }
};

export const updateTable = async (weddingId, tableIndex, tableData) => {
  try {
    const weddingRef = doc(db, 'weddings', weddingId);
    const weddingDoc = await getDoc(weddingRef);
    
    if (!weddingDoc.exists()) {
      throw new Error('Wedding not found');
    }
    
    const weddingData = weddingDoc.data();
    const tables = weddingData.tables || [];
    
    // Check if table exists at the specified index
    if (tableIndex < 0 || tableIndex >= tables.length) {
      throw new Error('Table not found');
    }
    
    // Update the table
    const updatedTables = [...tables];
    updatedTables[tableIndex] = {
      ...updatedTables[tableIndex],
      tableNumber: tableData.tableNumber || updatedTables[tableIndex].tableNumber,
      // Keep the existing members if not provided
      members: tableData.members || updatedTables[tableIndex].members
    };
    
    await updateDoc(weddingRef, { tables: updatedTables });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating table:', error);
    throw error;
  }
};

export const deleteTable = async (weddingId, tableIndex) => {
  try {
    const weddingRef = doc(db, 'weddings', weddingId);
    const weddingDoc = await getDoc(weddingRef);
    
    if (!weddingDoc.exists()) {
      throw new Error('Wedding not found');
    }
    
    const weddingData = weddingDoc.data();
    const tables = weddingData.tables || [];
    
    // Check if table exists at the specified index
    if (tableIndex < 0 || tableIndex >= tables.length) {
      throw new Error('Table not found');
    }
    
    // Remove the table at the specified index
    const updatedTables = [...tables];
    updatedTables.splice(tableIndex, 1);
    
    await updateDoc(weddingRef, { tables: updatedTables });
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting table:', error);
    throw error;
  }
};

// ===== MEMBERS/GUESTS =====
export const addMember = async (weddingId, tableIndex, memberName) => {
  try {
    const weddingRef = doc(db, 'weddings', weddingId);
    const weddingDoc = await getDoc(weddingRef);
    
    if (!weddingDoc.exists()) {
      throw new Error('Wedding not found');
    }
    
    const weddingData = weddingDoc.data();
    const tables = weddingData.tables || [];
    
    // Check if table exists at the specified index
    if (tableIndex < 0 || tableIndex >= tables.length) {
      throw new Error('Table not found');
    }
    
    // In your structure, members are just strings in an array
    // Add the member name to the table's members array
    const updatedTables = [...tables];
    updatedTables[tableIndex].members = [...(updatedTables[tableIndex].members || []), memberName];
    
    await updateDoc(weddingRef, { tables: updatedTables });
    
    return { success: true };
  } catch (error) {
    console.error('Error adding member:', error);
    throw error;
  }
};

export const updateMember = async (weddingId, tableIndex, memberIndex, newMemberName) => {
  try {
    const weddingRef = doc(db, 'weddings', weddingId);
    const weddingDoc = await getDoc(weddingRef);
    
    if (!weddingDoc.exists()) {
      throw new Error('Wedding not found');
    }
    
    const weddingData = weddingDoc.data();
    const tables = weddingData.tables || [];
    
    // Check if table exists at the specified index
    if (tableIndex < 0 || tableIndex >= tables.length) {
      throw new Error('Table not found');
    }
    
    const members = tables[tableIndex].members || [];
    
    // Check if member exists at the specified index
    if (memberIndex < 0 || memberIndex >= members.length) {
      throw new Error('Member not found');
    }
    
    // Update the member name
    const updatedTables = [...tables];
    updatedTables[tableIndex].members[memberIndex] = newMemberName;
    
    await updateDoc(weddingRef, { tables: updatedTables });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating member:', error);
    throw error;
  }
};

export const deleteMember = async (weddingId, tableIndex, memberIndex) => {
  try {
    const weddingRef = doc(db, 'weddings', weddingId);
    const weddingDoc = await getDoc(weddingRef);
    
    if (!weddingDoc.exists()) {
      throw new Error('Wedding not found');
    }
    
    const weddingData = weddingDoc.data();
    const tables = weddingData.tables || [];
    
    // Check if table exists at the specified index
    if (tableIndex < 0 || tableIndex >= tables.length) {
      throw new Error('Table not found');
    }
    
    const members = tables[tableIndex].members || [];
    
    // Check if member exists at the specified index
    if (memberIndex < 0 || memberIndex >= members.length) {
      throw new Error('Member not found');
    }
    
    // Remove the member at the specified index
    const updatedTables = [...tables];
    updatedTables[tableIndex].members.splice(memberIndex, 1);
    
    await updateDoc(weddingRef, { tables: updatedTables });
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting member:', error);
    throw error;
  }
};

// Function to assign a wedding to a user
export const assignWeddingToUser = async (weddingId, userId) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      weddings: arrayUnion(weddingId)
    });
    return { success: true };
  } catch (error) {
    console.error('Error assigning wedding to user:', error);
    throw error;
  }
};

// Function to remove a wedding assignment from a user
export const removeWeddingFromUser = async (weddingId, userId) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      weddings: arrayRemove(weddingId)
    });
    return { success: true };
  } catch (error) {
    console.error('Error removing wedding from user:', error);
    throw error;
  }
};

// Function to update public wedding data
const updatePublicWedding = async (weddingId, weddingData) => {
  try {
    // Copy all necessary data for public view
    const publicData = {
      name: weddingData.name,
      date: weddingData.date,
      location: weddingData.location,
      tables: weddingData.tables || [],
      lastUpdated: new Date()
    };
    
    await setDoc(doc(db, 'publicWeddings', weddingId), publicData);
    return { success: true };
  } catch (error) {
    console.error('Error updating public wedding:', error);
    throw error;
  }
};

// Function to resynchronize all weddings' public data
export const resyncAllPublicWeddings = async () => {
  try {
    const weddingsSnapshot = await getDocs(collection(db, 'weddings'));
    const updatePromises = weddingsSnapshot.docs.map(doc => 
      updatePublicWedding(doc.id, { id: doc.id, ...doc.data() })
    );
    await Promise.all(updatePromises);
    return { success: true };
  } catch (error) {
    console.error('Error resyncing public weddings:', error);
    throw error;
  }
};

// Function to add a table with multiple members
export const addTableWithMembers = async (weddingId, tableNumber, members) => {
  try {
    const weddingRef = doc(db, 'weddings', weddingId);
    const weddingDoc = await getDoc(weddingRef);
    
    if (!weddingDoc.exists()) {
      throw new Error('Wedding not found');
    }
    
    const weddingData = weddingDoc.data();
    const tables = weddingData.tables || [];
    
    // Create a new table with the members
    const newTable = {
      tableNumber: tableNumber,
      members: members
    };
    
    // Add the table to the wedding's tables array
    await updateDoc(weddingRef, {
      tables: [...tables, newTable]
    });
    
    // Update public wedding data
    await updatePublicWedding(weddingId, {
      ...weddingData,
      tables: [...tables, newTable]
    });
    
    return { tableId: tables.length, success: true };
  } catch (error) {
    console.error('Error adding table with members:', error);
    throw error;
  }
};
